import { useState } from "react";
import { MapPin, Phone, Mail, Globe, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Reveal, SectionLabel } from "./motion";

const inputCls = "w-full border border-[#0A1128]/15 bg-white px-4 py-3 text-sm outline-none focus:border-[#1D4ED8] transition-colors";

export default function Contact() {
  const [tab, setTab] = useState("enquiry");
  const [enquiry, setEnquiry] = useState({ name: "", email: "", phone: "", message: "" });
  const [resumeForm, setResumeForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", enquiry);
      toast.success("Enquiry received. Our team will reach out within one business day.");
      setEnquiry({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitResume = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please attach your resume (PDF, DOC or DOCX).");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(resumeForm).forEach(([k, v]) => fd.append(k, v));
      fd.append("resume", file);
      await api.post("/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resume submitted. A consultant will review it and contact you shortly.");
      setResumeForm({ name: "", email: "", phone: "", message: "" });
      setFile(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="grid grid-cols-1 lg:grid-cols-2" data-testid="contact-section">
      <div className="bg-[#0A1128] text-white py-24 md:py-32 px-6 md:px-12 lg:px-24 flex flex-col justify-between">
        <div>
          <SectionLabel number="06" text="Get In Touch" dark />
          <Reveal>
            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mb-12">
              Let's find your<br /><span className="text-[#1D4ED8]">next hire.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <img src="/fam-logo.png" alt="FAM Job Consultancy" className="h-20 w-auto mb-12" />
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="space-y-6 text-sm" data-testid="contact-details">
            <p className="flex items-start gap-4 text-white/70">
              <MapPin size={18} className="text-[#1D4ED8] shrink-0 mt-0.5" />
              64 Banshankari, Bengaluru, Karnataka, India
            </p>
            <p className="flex items-center gap-4 text-white/70">
              <Phone size={18} className="text-[#1D4ED8] shrink-0" />
              +91 60028 28049 · WhatsApp: 60028 28049
            </p>
            <p className="flex items-center gap-4 text-white/70">
              <Mail size={18} className="text-[#1D4ED8] shrink-0" />
              info@famjobconsultancy.com
            </p>
            <p className="flex items-center gap-4 text-white/70">
              <Globe size={18} className="text-[#1D4ED8] shrink-0" />
              www.famjobconsultancy.com
            </p>
          </div>
        </Reveal>
      </div>

      <div className="bg-[#F4F5F7] py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="flex border border-[#0A1128]/15 mb-10" data-testid="contact-tabs">
          {[
            { id: "enquiry", label: "Send an Enquiry" },
            { id: "resume", label: "Submit Resume" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                tab === t.id ? "bg-[#0A1128] text-white" : "text-[#475569] hover:bg-white"
              }`}
              data-testid={`contact-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "enquiry" ? (
          <form onSubmit={submitEnquiry} className="space-y-4" data-testid="contact-enquiry-form">
            <input required placeholder="Full name" value={enquiry.name} onChange={(e) => setEnquiry({ ...enquiry, name: e.target.value })} className={inputCls} data-testid="enquiry-name" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required type="email" placeholder="Email" value={enquiry.email} onChange={(e) => setEnquiry({ ...enquiry, email: e.target.value })} className={inputCls} data-testid="enquiry-email" />
              <input placeholder="Phone" value={enquiry.phone} onChange={(e) => setEnquiry({ ...enquiry, phone: e.target.value })} className={inputCls} data-testid="enquiry-phone" />
            </div>
            <textarea required rows={5} placeholder="Tell us about your hiring need or career goal" value={enquiry.message} onChange={(e) => setEnquiry({ ...enquiry, message: e.target.value })} className={inputCls} data-testid="enquiry-message" />
            <button disabled={busy} className="w-full bg-[#1D4ED8] hover:bg-[#1E3A8A] disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-4 transition-colors duration-300" data-testid="enquiry-submit">
              {busy ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitResume} className="space-y-4" data-testid="contact-resume-form">
            <input required placeholder="Full name" value={resumeForm.name} onChange={(e) => setResumeForm({ ...resumeForm, name: e.target.value })} className={inputCls} data-testid="resume-name" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required type="email" placeholder="Email" value={resumeForm.email} onChange={(e) => setResumeForm({ ...resumeForm, email: e.target.value })} className={inputCls} data-testid="resume-email" />
              <input placeholder="Phone" value={resumeForm.phone} onChange={(e) => setResumeForm({ ...resumeForm, phone: e.target.value })} className={inputCls} data-testid="resume-phone" />
            </div>
            <textarea rows={3} placeholder="Target role, experience, notice period (optional)" value={resumeForm.message} onChange={(e) => setResumeForm({ ...resumeForm, message: e.target.value })} className={inputCls} data-testid="resume-message" />
            <label className="flex items-center justify-center gap-3 border border-dashed border-[#0A1128]/25 hover:border-[#1D4ED8] bg-white px-4 py-8 cursor-pointer transition-colors" data-testid="resume-file-drop">
              <UploadCloud size={20} className="text-[#1D4ED8]" />
              <span className="text-sm text-[#475569]">{file ? file.name : "Attach resume — PDF, DOC, DOCX (max 5 MB)"}</span>
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="resume-file-input" />
            </label>
            <button disabled={busy} className="w-full bg-[#1D4ED8] hover:bg-[#1E3A8A] disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-4 transition-colors duration-300" data-testid="resume-submit">
              {busy ? "Uploading…" : "Submit Resume"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
